import React from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { Button, IconButton, Surface, useTheme } from 'react-native-paper';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import { Job } from '../types/Job';
import { formatAmount } from '../utils/formatAmount';
import { formatRelativeDate } from '../utils/formatDate';
import { initialsFor } from '../utils/initials';
import { useAppTheme } from '../theme/theme';

interface JobCardProps {
  job: Job;
  truckMap?: Map<string, string>;
  onPress?: (job: Job) => void;
  onDelete?: (job: Job) => void;
  onPickUp?: (job: Job) => void;
  pickUpDisabled?: boolean;
  onComplete?: (job: Job) => void;
  onRelease?: (job: Job) => void;
}

interface TypeIcon {
  icon: string;
  bg: string;
  fg: string;
}

function firstName(name?: string): string | undefined {
  return name?.trim().split(/\s+/)[0];
}

const JobCard: React.FC<JobCardProps> = ({
  job,
  truckMap,
  onPress,
  onDelete,
  onPickUp,
  pickUpDisabled,
  onComplete,
  onRelease,
}) => {
  const theme = useAppTheme();

  const typeIcon: TypeIcon = ((): TypeIcon => {
    if (job.status === 'completed') {
      if (job.paymentType === 'debt') {
        return {
          icon: 'clock-alert-outline',
          bg: theme.colors.errorContainer,
          fg: theme.colors.onErrorContainer,
        };
      }
      return {
        icon: 'check-decagram',
        bg: theme.colors.successContainer,
        fg: theme.colors.onSuccessContainer,
      };
    }
    if (job.status === 'assigned') {
      return {
        icon: 'truck-fast',
        bg: theme.colors.secondaryContainer,
        fg: theme.colors.onSecondaryContainer,
      };
    }
    return {
      icon: 'package-variant',
      bg: theme.colors.primaryContainer,
      fg: theme.colors.onPrimaryContainer,
    };
  })();

  const isCompleted = job.status === 'completed';
  const amountSign = isCompleted ? (job.paymentType === 'debt' ? '−' : '+') : '';
  const amountColor =
    isCompleted && job.paymentType === 'debt'
      ? theme.colors.error
      : isCompleted
      ? theme.colors.success
      : theme.colors.primary;

  const inlineMeta =
    job.description?.trim() ||
    (job.cargoTags.length > 0 ? job.cargoTags.join(', ') : '');

  const creatorName = job.createdByName;
  const driverName = job.assignedDriverName;
  const completerUid = job.completedBy ?? (isCompleted ? job.assignedDriverId : undefined);
  const isRetro = isCompleted && !!completerUid && completerUid === job.createdBy;
  const completerName =
    job.completedByName ?? (isRetro ? job.createdByName : job.assignedDriverName);

  const plate = job.assignedTruckId ? truckMap?.get(job.assignedTruckId) : undefined;
  const tailDate = isCompleted ? job.completedAt ?? job.createdAt : job.createdAt;
  const tail = [plate, formatRelativeDate(tailDate)].filter(Boolean).join(' · ');

  const card = (
    <Surface
      style={[styles.card, { backgroundColor: theme.colors.surface }]}
      elevation={1}
      testID={`job-card-${job.id}`}
    >
      <View style={styles.titleRow}>
        <Text
          style={[styles.title, { color: theme.colors.onSurface }]}
          numberOfLines={1}
        >
          {job.from} → {job.to}
        </Text>
        {onDelete && (
          <IconButton
            icon="trash-can-outline"
            size={18}
            onPress={() => onDelete(job)}
            iconColor={theme.colors.error}
            style={styles.titleDelete}
            testID={`delete-job-${job.id}`}
          />
        )}
      </View>

      <View style={styles.row2}>
        <View style={styles.row2Left}>
          <View style={[styles.typeIcon, { backgroundColor: typeIcon.bg }]}>
            <MaterialCommunityIcons name={typeIcon.icon} size={14} color={typeIcon.fg} />
          </View>
          {!!inlineMeta && (
            <Text
              style={[styles.inlineMeta, { color: theme.colors.onSurfaceVariant }]}
              numberOfLines={1}
            >
              {inlineMeta}
            </Text>
          )}
        </View>
        <View style={styles.amountCol}>
          <Text style={[styles.amount, { color: amountColor }]}>
            {amountSign}
            {formatAmount(isCompleted ? job.amount : job.intakeAmount)}
          </Text>
          {isCompleted && job.serviceCost !== undefined && (
            <Text
              style={[styles.serviceCost, { color: theme.colors.onSurfaceVariant }]}
            >
              Cost: {formatAmount(job.serviceCost)}
            </Text>
          )}
        </View>
      </View>

      <View style={[styles.footer, { borderTopColor: theme.colors.outlineVariant }]}>
        <View style={styles.people}>
          {isRetro && creatorName ? (
            <PeopleChip
              name={firstName(creatorName)}
              icons={['pencil-outline', 'check-circle-outline']}
              avatarText={initialsFor(creatorName)}
            />
          ) : !isRetro ? (
            <>
              {creatorName && (
                <PeopleChip
                  name={firstName(creatorName)}
                  icons={['pencil-outline']}
                  avatarText={initialsFor(creatorName)}
                />
              )}
              {!isCompleted && driverName && (
                <PeopleChip
                  name={firstName(driverName)}
                  icons={['steering']}
                  avatarText={initialsFor(driverName)}
                />
              )}
              {isCompleted && completerName && completerUid !== job.createdBy && (
                <PeopleChip
                  name={firstName(completerName)}
                  icons={['check-circle-outline']}
                  avatarText={initialsFor(completerName)}
                />
              )}
            </>
          ) : null}
        </View>
        {!!tail && (
          <Text style={[styles.tail, { color: theme.colors.onSurfaceVariant }]}>{tail}</Text>
        )}
      </View>

      {onPickUp && (
        <View style={styles.actionRow}>
          <Button
            mode="contained"
            icon="truck-fast"
            // Guard kept: RNTL fireEvent.press bypasses the Paper Button disabled prop in tests.
            onPress={() => !pickUpDisabled && onPickUp(job)}
            disabled={pickUpDisabled}
            style={styles.pickUpButton}
            testID={`pickup-button-${job.id}`}
          >
            Pick Up
          </Button>
        </View>
      )}

      {(onRelease || onComplete) && (
        <View style={styles.dualActionRow}>
          {onRelease && (
            <Button
              mode="outlined"
              icon="undo-variant"
              onPress={() => onRelease(job)}
              style={styles.flexButton}
              testID={`release-button-${job.id}`}
            >
              Release
            </Button>
          )}
          {onComplete && (
            <Button
              mode="contained"
              icon="flag-checkered"
              onPress={() => onComplete(job)}
              style={styles.flexButton}
              testID={`complete-button-${job.id}`}
            >
              Complete
            </Button>
          )}
        </View>
      )}
    </Surface>
  );

  if (onPress) {
    return (
      <TouchableOpacity activeOpacity={0.75} onPress={() => onPress(job)}>
        {card}
      </TouchableOpacity>
    );
  }
  return card;
};

interface PeopleChipProps {
  name?: string;
  icons: string[];
  avatarText: string;
}

const PeopleChip: React.FC<PeopleChipProps> = ({ name, icons, avatarText }) => {
  const theme = useTheme();
  return (
    <View style={[styles.chip, { backgroundColor: theme.colors.surfaceVariant }]}>
      {icons.map(icon => (
        <MaterialCommunityIcons
          key={icon}
          name={icon}
          size={11}
          color={theme.colors.onSurfaceVariant}
          style={styles.chipIcon}
        />
      ))}
      <View style={[styles.avatar, { backgroundColor: theme.colors.secondaryContainer }]}>
        <Text
          numberOfLines={1}
          allowFontScaling={false}
          style={[styles.avatarText, { color: theme.colors.primary }]}
        >
          {avatarText}
        </Text>
      </View>
      {!!name && (
        <Text style={[styles.chipName, { color: theme.colors.onSurfaceVariant }]}>
          {name}
        </Text>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  card: { borderRadius: 12, padding: 14 },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  title: {
    fontFamily: 'Manrope-SemiBold',
    fontSize: 16,
    lineHeight: 22,
    flex: 1,
    minWidth: 0,
  },
  titleDelete: {
    margin: 0,
    marginRight: -8,
  },
  row2: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 6,
    gap: 10,
  },
  row2Left: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    flex: 1,
    minWidth: 0,
  },
  typeIcon: {
    width: 22,
    height: 22,
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
  },
  inlineMeta: {
    fontFamily: 'Inter-Regular',
    fontSize: 12,
    flexShrink: 1,
  },
  amountCol: {
    alignItems: 'flex-end',
  },
  amount: {
    fontFamily: 'Manrope-SemiBold',
    fontSize: 15,
  },
  serviceCost: {
    fontFamily: 'Inter-Regular',
    fontSize: 11,
    marginTop: 1,
  },
  footer: {
    marginTop: 10,
    paddingTop: 8,
    borderTopWidth: StyleSheet.hairlineWidth,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  },
  people: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
    flex: 1,
    minWidth: 0,
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingLeft: 4,
    paddingRight: 8,
    paddingVertical: 2,
    borderRadius: 9999,
    gap: 4,
  },
  chipIcon: {
    opacity: 0.85,
  },
  avatar: {
    width: 22,
    height: 22,
    borderRadius: 11,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  avatarText: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 11,
    lineHeight: 13,
    letterSpacing: -0.2,
    textAlign: 'center',
    textAlignVertical: 'center',
    includeFontPadding: false,
  },
  chipName: {
    fontFamily: 'Inter-Medium',
    fontSize: 11,
  },
  tail: {
    fontFamily: 'Inter-Regular',
    fontSize: 11,
  },
  actionRow: {
    marginTop: 8,
  },
  pickUpButton: {
    borderRadius: 6,
  },
  dualActionRow: {
    marginTop: 8,
    flexDirection: 'row',
    gap: 8,
  },
  flexButton: {
    flex: 1,
    borderRadius: 6,
  },
});

export default JobCard;
